/**
 * Hardware Monitor Dashboard
 * Admin-Dashboard für Intecio GmbH - Überwachung aller Hardware-Geräte
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { getHardwareMonitor, getDeviceDetails, createHardwareDevice, updateHardwareDevice, deleteHardwareDevice, type HardwareMonitorResponse, type HardwareDevice } from "../../api/hardwareMonitor";
import { getCameraReferences, uploadCameraReference, deleteCameraReference, type CameraReference } from "../../api/cameraReferences";
import "./HardwareMonitorPage.css";

export function HardwareMonitorPage() {
  const [data, setData] = useState<HardwareMonitorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState<HardwareDevice | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const saveTimerRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [emptyReferences, setEmptyReferences] = useState<CameraReference[]>([]);
  const [trucksReferences, setTrucksReferences] = useState<CameraReference[]>([]);
  const [loadingEmptyReferences, setLoadingEmptyReferences] = useState(false);
  const [loadingTrucksReferences, setLoadingTrucksReferences] = useState(false);
  const [uploadingEmptyReference, setUploadingEmptyReference] = useState(false);
  const [uploadingTrucksReference, setUploadingTrucksReference] = useState(false);
  const emptyFileInputRef = useRef<HTMLInputElement>(null);
  const trucksFileInputRef = useRef<HTMLInputElement>(null);
  const [sortField, setSortField] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getHardwareMonitor();
      setData(result);
      setError(null);
    } catch (err) {
      console.error("Failed to load hardware monitor:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  if (loading && !data) {
    return (
      <div className="hardware-monitor">
        <div className="loading">Loading hardware monitor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hardware-monitor">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Filter and sort devices
  const filteredDevices = data.devices
    .filter((device) => {
      if (selectedTenant !== "all" && device.tenantId !== selectedTenant) return false;
      if (selectedType !== "all" && device.hardwareType !== selectedType) return false;
      if (searchQuery && !device.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let aValue: any = a[sortField as keyof HardwareDevice];
      let bValue: any = b[sortField as keyof HardwareDevice];

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) aValue = "";
      if (bValue === undefined || bValue === null) bValue = "";

      // Convert to lowercase for string comparison
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      // Compare
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortDirection === "asc" ? comparison : -comparison;
    });

  // Get unique tenants with names
  const tenantMap = new Map<string, string>();
  data.devices.forEach((d) => {
    if (!tenantMap.has(d.tenantId)) {
      tenantMap.set(d.tenantId, d.tenantName || d.tenantId);
    }
  });
  const tenants = Array.from(tenantMap.entries())
    .sort((a, b) => a[1].localeCompare(b[1]));

  // Calculate types and counts from filtered devices (by tenant only, not by type)
  const devicesFilteredByTenant = data.devices.filter((device) => {
    if (selectedTenant !== "all" && device.tenantId !== selectedTenant) return false;
    return true;
  });

  const typeCountMap = new Map<string, number>();
  devicesFilteredByTenant.forEach((d) => {
    const count = typeCountMap.get(d.hardwareType) || 0;
    typeCountMap.set(d.hardwareType, count + 1);
  });
  const availableTypes = Array.from(typeCountMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]));

  // Handle device row click
  const handleDeviceClick = async (device: HardwareDevice) => {
    try {
      // Fetch fresh device data from API
      const freshDevice = await getDeviceDetails(device._id);
      setSelectedDevice(freshDevice);

      // Load camera references if it's a camera
      if (freshDevice.hardwareType === "camera" || (freshDevice.hardwareType && freshDevice.hardwareType.toLowerCase().includes("camera"))) {
        await loadEmptyReferences(freshDevice._id);
        await loadTrucksReferences(freshDevice._id);
      }
    } catch (err) {
      console.error("Failed to load device details:", err);
      // Fallback to device from list
      setSelectedDevice(device);
    }
  };

  // Close detail view
  const handleCloseDetail = () => {
    setSelectedDevice(null);
    setEditValues({});
    setEmptyReferences([]);
    setTrucksReferences([]);
    // Clear all pending timers
    Object.values(saveTimerRef.current).forEach(timer => clearTimeout(timer));
    saveTimerRef.current = {};
  };

  // Handle field edit with auto-save
  const handleFieldEdit = (fieldName: string, value: any) => {
    if (!selectedDevice) return;

    // Update local state immediately
    setEditValues(prev => ({ ...prev, [fieldName]: value }));

    // Clear existing timer for this field
    if (saveTimerRef.current[fieldName]) {
      clearTimeout(saveTimerRef.current[fieldName]);
    }

    // Set new timer for auto-save after 2 seconds
    saveTimerRef.current[fieldName] = setTimeout(async () => {
      try {
        const updates: any = {};
        updates[fieldName] = value;

        await updateHardwareDevice(selectedDevice._id, updates);

        // Update selected device with new value
        setSelectedDevice(prev => prev ? { ...prev, [fieldName]: value } : null);

        // Show success indicator
        setShowSavedIndicator(true);
        setTimeout(() => setShowSavedIndicator(false), 2000);

        // Remove from edit values after successful save
        setEditValues(prev => {
          const newValues = { ...prev };
          delete newValues[fieldName];
          return newValues;
        });
      } catch (err) {
        console.error(`Failed to update ${fieldName}:`, err);
        alert(`Failed to save ${fieldName}: ${err instanceof Error ? err.message : "Unknown error"}`);
      }
    }, 2000);
  };

  // Get current value for a field (either editing or saved)
  const getFieldValue = (fieldName: string) => {
    if (editValues[fieldName] !== undefined) {
      return editValues[fieldName];
    }
    return selectedDevice?.[fieldName as keyof HardwareDevice] ?? "";
  };

  // Camera References Functions
  const loadEmptyReferences = async (deviceId: string) => {
    setLoadingEmptyReferences(true);
    try {
      const result = await getCameraReferences(deviceId, 'empty');
      setEmptyReferences(result.references);
    } catch (err) {
      console.error("Failed to load empty references:", err);
      setEmptyReferences([]);
    } finally {
      setLoadingEmptyReferences(false);
    }
  };

  const loadTrucksReferences = async (deviceId: string) => {
    setLoadingTrucksReferences(true);
    try {
      const result = await getCameraReferences(deviceId, 'trucks');
      setTrucksReferences(result.references);
    } catch (err) {
      console.error("Failed to load trucks references:", err);
      setTrucksReferences([]);
    } finally {
      setLoadingTrucksReferences(false);
    }
  };

  const handleEmptyFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDevice || !e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        setUploadingEmptyReference(true);
        const base64 = reader.result as string;

        await uploadCameraReference(
          selectedDevice._id,
          'empty',
          file.name,
          base64,
          file.type
        );

        // Reload references
        await loadEmptyReferences(selectedDevice._id);

        // Reset file input
        if (emptyFileInputRef.current) {
          emptyFileInputRef.current.value = "";
        }
      } catch (err) {
        console.error("Failed to upload empty reference:", err);
        alert(`Upload failed: ${err instanceof Error ? err.message : "Unknown error"}`);
      } finally {
        setUploadingEmptyReference(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleTrucksFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDevice || !e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        setUploadingTrucksReference(true);
        const base64 = reader.result as string;

        await uploadCameraReference(
          selectedDevice._id,
          'trucks',
          file.name,
          base64,
          file.type
        );

        // Reload references
        await loadTrucksReferences(selectedDevice._id);

        // Reset file input
        if (trucksFileInputRef.current) {
          trucksFileInputRef.current.value = "";
        }
      } catch (err) {
        console.error("Failed to upload trucks reference:", err);
        alert(`Upload failed: ${err instanceof Error ? err.message : "Unknown error"}`);
      } finally {
        setUploadingTrucksReference(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleDeleteEmptyReference = async (fileName: string) => {
    if (!selectedDevice) return;
    if (!confirm(`Leeres Referenzbild "${fileName}" löschen?`)) return;

    try {
      await deleteCameraReference(selectedDevice._id, 'empty', fileName);
      await loadEmptyReferences(selectedDevice._id);
    } catch (err) {
      console.error("Failed to delete empty reference:", err);
      alert(`Delete failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  };

  const handleDeleteTrucksReference = async (fileName: string) => {
    if (!selectedDevice) return;
    if (!confirm(`LKW Referenzbild "${fileName}" löschen?`)) return;

    try {
      await deleteCameraReference(selectedDevice._id, 'trucks', fileName);
      await loadTrucksReferences(selectedDevice._id);
    } catch (err) {
      console.error("Failed to delete trucks reference:", err);
      alert(`Delete failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  };

  // Handle device deletion
  const handleDeleteDevice = async () => {
    if (!selectedDevice) return;

    const confirmed = confirm(
      `Are you sure you want to delete "${selectedDevice.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteHardwareDevice(selectedDevice._id);

      // Close detail view
      setSelectedDevice(null);

      // Reload data
      await loadData();
    } catch (err) {
      console.error("Failed to delete device:", err);
      alert(`Delete failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  };

  // Handle form submit
  const handleAddDevice = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const newDevice = {
        type: formData.get("type") as "hardware" | "hardwareConnector",
        hardwareType: formData.get("hardwareType") as "connector" | "terminal",
        connectorName: formData.get("connectorName") as string || undefined,
        deviceName: formData.get("deviceName") as string || undefined,
        tenantId: formData.get("tenantId") as string,
        ip: formData.get("ip") as string || undefined,
        port: formData.get("port") ? parseInt(formData.get("port") as string) : undefined,
        location: formData.get("location") as string || undefined,
        plant: formData.get("plant") as string || undefined,
      };

      await createHardwareDevice(newDevice);

      // Reload data
      await loadData();

      // Close modal
      setShowAddModal(false);

      // Reset form (will happen automatically when modal closes)
    } catch (err) {
      console.error("Failed to create hardware device:", err);
      alert(`Failed to create device: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show device detail view
  if (selectedDevice) {
    return (
      <div className="hardware-monitor">
        <div className="detail-header">
          <button onClick={handleCloseDetail} className="back-btn">
            ← Back to Overview
          </button>
          <input
            type="text"
            value={getFieldValue("name")}
            onChange={(e) => handleFieldEdit("name", e.target.value)}
            className="editable-device-name"
            placeholder="Device Name"
          />
          <button onClick={handleDeleteDevice} className="delete-device-btn">
            🗑️ Delete Device
          </button>
          {showSavedIndicator && (
            <div className="saved-indicator">
              ✓ Saved
            </div>
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-card status-card">
            <h3>Status</h3>
            <div className="detail-value">
              <select
                value={getFieldValue("status")}
                onChange={(e) => handleFieldEdit("status", e.target.value)}
                className="editable-select"
              >
                <option value="online">🟢 online</option>
                <option value="offline">🔴 offline</option>
                <option value="never-connected">⚪ never-connected</option>
                <option value="unknown">unknown</option>
              </select>
            </div>
          </div>

          <div className="detail-card">
            <h3>Type</h3>
            <div className="detail-value">
              <input
                type="text"
                list="hardwareTypes"
                value={getFieldValue("hardwareType")}
                onChange={(e) => handleFieldEdit("hardwareType", e.target.value)}
                className="editable-input"
                placeholder="Select or type custom value"
              />
              <datalist id="hardwareTypes">
                <option value="connector" />
                <option value="terminal" />
                <option value="camera" />
              </datalist>
            </div>
          </div>

          <div className="detail-card">
            <h3>Tenant</h3>
            <div className="detail-value non-editable">{selectedDevice.tenantName || selectedDevice.tenantId}</div>
          </div>

          <div className="detail-card">
            <h3>Last Seen</h3>
            <div className="detail-value non-editable">
              {selectedDevice.lastSeen ? new Date(selectedDevice.lastSeen).toLocaleString() : "Never"}
              <div className="detail-sublabel">{selectedDevice.lastSeenAgo}</div>
            </div>
          </div>

          <div className="detail-card">
            <h3>Certificate</h3>
            <div className="detail-value non-editable">
              <span className={`cert-badge ${selectedDevice.certificateStatus}`}>
                {selectedDevice.certificateStatus === "valid" ? "✅ Valid" :
                  selectedDevice.certificateStatus === "expiring-soon" ? "⚠️ Expiring Soon" :
                    selectedDevice.certificateStatus === "expired" ? "❌ Expired" : "➖ No Certificate"}
              </span>
              {selectedDevice.certificateExpiry && (
                <div className="detail-sublabel">
                  Expires: {new Date(selectedDevice.certificateExpiry).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          <div className="detail-card">
            <h3>IP Address</h3>
            <div className="detail-value">
              <input
                type="text"
                value={getFieldValue("ip")}
                onChange={(e) => handleFieldEdit("ip", e.target.value)}
                className="editable-input"
                placeholder="192.168.1.100"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Port</h3>
            <div className="detail-value">
              <input
                type="number"
                value={getFieldValue("port")}
                onChange={(e) => handleFieldEdit("port", parseInt(e.target.value) || "")}
                className="editable-input"
                placeholder="8080"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Location</h3>
            <div className="detail-value">
              <input
                type="text"
                value={getFieldValue("location")}
                onChange={(e) => handleFieldEdit("location", e.target.value)}
                className="editable-input"
                placeholder="Warehouse A"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Plant (Werk)</h3>
            <div className="detail-value">
              <input
                type="text"
                value={getFieldValue("plant")}
                onChange={(e) => handleFieldEdit("plant", e.target.value)}
                className="editable-input"
                placeholder="0122"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Connector Name</h3>
            <div className="detail-value">
              <input
                type="text"
                value={getFieldValue("connectorName")}
                onChange={(e) => handleFieldEdit("connectorName", e.target.value)}
                className="editable-input"
                placeholder="Connector Post"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Device Name</h3>
            <div className="detail-value">
              <input
                type="text"
                value={getFieldValue("deviceName")}
                onChange={(e) => handleFieldEdit("deviceName", e.target.value)}
                className="editable-input"
                placeholder="Terminal 1"
              />
            </div>
          </div>

          <div className="detail-card">
            <h3>Device ID</h3>
            <div className="detail-value device-id non-editable">{selectedDevice._id}</div>
          </div>

          {selectedDevice.metadata && Object.keys(selectedDevice.metadata).length > 0 && (
            <div className="detail-card metadata-card">
              <h3>Metadata</h3>
              <div className="metadata-content">
                <pre>{JSON.stringify(selectedDevice.metadata, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Camera Reference Pictures Section */}
          {(selectedDevice.hardwareType === "camera" || (selectedDevice.hardwareType && selectedDevice.hardwareType.toLowerCase().includes("camera"))) && (
            <>
              {/* Empty Reference Picture */}
              <div className="detail-card references-card">
                <h3>Leeres Referenzbild</h3>
                <p className="references-description">
                  Nur ein Bild erlaubt. Wird für Hintergrundsubtraktion verwendet.
                  Speicherort: <code>{selectedDevice.tenantId}/{selectedDevice._id}/empty/</code>
                </p>

                <div className="references-actions">
                  <input
                    ref={emptyFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleEmptyFileSelect}
                    style={{ display: "none" }}
                  />
                  <button
                    onClick={() => emptyFileInputRef.current?.click()}
                    className="upload-btn"
                    disabled={uploadingEmptyReference}
                  >
                    {uploadingEmptyReference ? "Uploading..." : emptyReferences.length > 0 ? "🔄 Bild ersetzen" : "📤 Leeres Bild hochladen"}
                  </button>
                </div>

                <div className="references-list">
                  {loadingEmptyReferences && <div className="loading-references">Loading...</div>}

                  {!loadingEmptyReferences && emptyReferences.length === 0 && (
                    <div className="no-references">Noch kein leeres Referenzbild hochgeladen.</div>
                  )}

                  {!loadingEmptyReferences && emptyReferences.length > 0 && (
                    <div className="reference-grid">
                      {emptyReferences.map((ref) => (
                        <div key={ref.fileName} className="reference-item">
                          <div className="reference-image">
                            {ref.sasUrl ? (
                              <img src={ref.sasUrl} alt={ref.fileName} />
                            ) : (
                              <div className="no-preview">No preview</div>
                            )}
                          </div>
                          <div className="reference-info">
                            <div className="reference-name">{ref.fileName}</div>
                            <div className="reference-meta">
                              {ref.size && <span>{(ref.size / 1024).toFixed(1)} KB</span>}
                              {ref.createdAt && (
                                <span>{new Date(ref.createdAt).toLocaleDateString()}</span>
                              )}
                            </div>
                            <button
                              onClick={() => handleDeleteEmptyReference(ref.fileName)}
                              className="delete-reference-btn"
                            >
                              🗑️ Löschen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Trucks Reference Pictures */}
              <div className="detail-card references-card">
                <h3>LKW Beispielbilder</h3>
                <p className="references-description">
                  Mehrere Bilder erlaubt. Werden für Mustervergleich und Training verwendet.
                  Speicherort: <code>{selectedDevice.tenantId}/{selectedDevice._id}/trucks/</code>
                </p>

                <div className="references-actions">
                  <input
                    ref={trucksFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleTrucksFileSelect}
                    style={{ display: "none" }}
                  />
                  <button
                    onClick={() => trucksFileInputRef.current?.click()}
                    className="upload-btn"
                    disabled={uploadingTrucksReference}
                  >
                    {uploadingTrucksReference ? "Uploading..." : "📤 LKW Bild hochladen"}
                  </button>
                </div>

                <div className="references-list">
                  {loadingTrucksReferences && <div className="loading-references">Loading...</div>}

                  {!loadingTrucksReferences && trucksReferences.length === 0 && (
                    <div className="no-references">Noch keine LKW Bilder hochgeladen.</div>
                  )}

                  {!loadingTrucksReferences && trucksReferences.length > 0 && (
                    <div className="reference-grid">
                      {trucksReferences.map((ref) => (
                        <div key={ref.fileName} className="reference-item">
                          <div className="reference-image">
                            {ref.sasUrl ? (
                              <img src={ref.sasUrl} alt={ref.fileName} />
                            ) : (
                              <div className="no-preview">No preview</div>
                            )}
                          </div>
                          <div className="reference-info">
                            <div className="reference-name">{ref.fileName}</div>
                            <div className="reference-meta">
                              {ref.size && <span>{(ref.size / 1024).toFixed(1)} KB</span>}
                              {ref.createdAt && (
                                <span>{new Date(ref.createdAt).toLocaleDateString()}</span>
                              )}
                            </div>
                            <button
                              onClick={() => handleDeleteTrucksReference(ref.fileName)}
                              className="delete-reference-btn"
                            >
                              🗑️ Löschen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="hardware-monitor">
      <header className="monitor-header">
        <h1>Hardware Monitor</h1>
        <div className="header-controls">
          <button onClick={loadData} className="refresh-btn" title="Refresh">
            🔄 Refresh
          </button>
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <span className="last-update">Last: {new Date(data.timestamp).toLocaleTimeString()}</span>
        </div>
      </header>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <h3>Total Devices</h3>
          <div className="stat-value">{data.stats.total}</div>
        </div>

        <div className="stat-card online">
          <h3>Online</h3>
          <div className="stat-value">{data.stats.byStatus.online}</div>
        </div>

        <div className="stat-card offline">
          <h3>Offline</h3>
          <div className="stat-value">{data.stats.byStatus.offline}</div>
        </div>

        <div className="stat-card never-connected">
          <h3>Never Connected</h3>
          <div className="stat-value">{data.stats.byStatus.neverConnected}</div>
        </div>

        <div className="stat-card cert-expired">
          <h3>Cert Expired</h3>
          <div className="stat-value">{data.stats.certificateIssues.expired}</div>
        </div>

        <div className="stat-card cert-expiring">
          <h3>Cert Expiring Soon</h3>
          <div className="stat-value">{data.stats.certificateIssues.expiringSoon}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />

        <select value={selectedTenant} onChange={(e) => setSelectedTenant(e.target.value)} className="filter-select">
          <option value="all">All Tenants</option>
          {tenants.map(([tenantId, tenantName]) => (
            <option key={tenantId} value={tenantId}>
              {tenantName} ({data.stats.byTenant[tenantId]})
            </option>
          ))}
        </select>

        <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="filter-select">
          <option value="all">All Types</option>
          {availableTypes.map(([type, count]) => (
            <option key={type} value={type}>
              {type} ({count})
            </option>
          ))}
        </select>

        <button onClick={() => setShowAddModal(true)} className="add-btn">
          + Add Hardware
        </button>
      </div>

      {/* Devices Table */}
      <div className="devices-table-container">
        <table className="devices-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("status")} className="sortable">
                Status {sortField === "status" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("name")} className="sortable">
                Name {sortField === "name" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("hardwareType")} className="sortable">
                Gerätetyp {sortField === "hardwareType" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("tenantId")} className="sortable">
                Tenant {sortField === "tenantId" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("lastSeen")} className="sortable">
                Zuletzt gesehen {sortField === "lastSeen" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("certificateStatus")} className="sortable">
                Zertifikat {sortField === "certificateStatus" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("plant")} className="sortable">
                Werk {sortField === "plant" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("location")} className="sortable">
                Standort {sortField === "location" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("ip")} className="sortable">
                IP {sortField === "ip" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDevices.map((device) => (
              <tr
                key={device._id}
                className={`device-row ${device.status}`}
                onClick={() => handleDeviceClick(device)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <span className={`status-badge ${device.status}`}>
                    {device.status === "online" ? "🟢" : device.status === "offline" ? "🔴" : "⚪"}
                    {device.status}
                  </span>
                </td>
                <td className="device-name">{device.name}</td>
                <td>{device.hardwareType}</td>
                <td>{device.tenantName || device.tenantId}</td>
                <td>{device.lastSeen ? new Date(device.lastSeen).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                <td>
                  <span className={`cert-badge ${device.certificateStatus}`}>
                    {device.certificateStatus === "valid" ? "✅" :
                      device.certificateStatus === "expiring-soon" ? "⚠️" :
                        device.certificateStatus === "expired" ? "❌" : "➖"}
                  </span>
                </td>
                <td>{device.plant || "-"}</td>
                <td>{device.location || "-"}</td>
                <td>{device.ip ? `${device.ip}:${device.port}` : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredDevices.length === 0 && (
          <div className="no-results">
            No devices found matching your filters.
          </div>
        )}
      </div>

      {/* Add Hardware Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Hardware Device</h2>
              <button onClick={() => setShowAddModal(false)} className="close-btn">×</button>
            </div>
            <form onSubmit={handleAddDevice}>
              <div className="form-group">
                <label>Type *</label>
                <select name="type" required>
                  <option value="hardware">Hardware</option>
                  <option value="hardwareConnector">Hardware Connector</option>
                </select>
              </div>

              <div className="form-group">
                <label>Hardware Type *</label>
                <input
                  type="text"
                  name="hardwareType"
                  list="hardwareTypes"
                  required
                  placeholder="Select or type custom value"
                />
                <datalist id="hardwareTypes">
                  <option value="connector" />
                  <option value="terminal" />
                </datalist>
              </div>

              <div className="form-group">
                <label>Connector Name</label>
                <input type="text" name="connectorName" placeholder="e.g., Connector Post" />
              </div>

              <div className="form-group">
                <label>Device Name</label>
                <input type="text" name="deviceName" placeholder="e.g., Terminal 1" />
              </div>

              <div className="form-group">
                <label>Tenant ID *</label>
                <input type="text" name="tenantId" required placeholder="e.g., 693a810a08eca29d54af2734" />
              </div>

              <div className="form-group">
                <label>IP Address</label>
                <input type="text" name="ip" placeholder="e.g., 192.168.1.100" />
              </div>

              <div className="form-group">
                <label>Port</label>
                <input type="number" name="port" placeholder="e.g., 8080" />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input type="text" name="location" placeholder="e.g., Warehouse A" />
              </div>

              <div className="form-group">
                <label>Plant (Werk)</label>
                <input type="text" name="plant" placeholder="e.g., 0122" />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddModal(false)} className="cancel-btn" disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Add Device"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
